import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InformationCircleIcon, FolderIcon, Cog6ToothIcon, UserCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  getIdentitySettings,
  updateIdentitySettings,
  uploadIdentityAvatar,
  deleteIdentityAvatar,
} from '../moduleServices/identity_services';

const MainSettings = ({ summary, onSettingsSaved }) => {
  const [instanceRoot, setInstanceRoot] = useState('');
  const [stateRelpath, setStateRelpath] = useState('self/STATE.md');
  const [statePath, setStatePath] = useState('');
  const [identityName, setIdentityName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [error, setError] = useState(null);
  const [avatarError, setAvatarError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  const fileInputRef = useRef(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getIdentitySettings();
      setInstanceRoot(data.instance_root || '');
      setStateRelpath(data.state_relpath || 'self/STATE.md');
      setStatePath(data.state_path || '');
      setIdentityName(data.identity_name || '');
      setAvatarUrl(data.avatar_url || null);
    } catch (err) {
      console.error('Error loading identity settings:', err);
      setError(err?.response?.data?.detail || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const data = await updateIdentitySettings({
        instance_root: instanceRoot,
        state_relpath: stateRelpath,
        identity_name: identityName,
      });
      setInstanceRoot(data.instance_root || '');
      setStateRelpath(data.state_relpath || 'self/STATE.md');
      setStatePath(data.state_path || '');
      setIdentityName(data.identity_name || '');
      setAvatarUrl(data.avatar_url || null);
      setSavedMsg('Settings saved.');
      onSettingsSaved?.();
    } catch (err) {
      console.error('Error saving identity settings:', err);
      setError(err?.response?.data?.detail || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarSaving(true);
    setAvatarError(null);
    try {
      const data = await uploadIdentityAvatar(file);
      setAvatarUrl(data.avatar_url || null);
      onSettingsSaved?.();
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setAvatarError(err?.response?.data?.detail || 'Failed to upload avatar.');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleAvatarDelete = async () => {
    setAvatarSaving(true);
    setAvatarError(null);
    try {
      const data = await deleteIdentityAvatar();
      setAvatarUrl(data.avatar_url || null);
      onSettingsSaved?.();
    } catch (err) {
      console.error('Error removing avatar:', err);
      setAvatarError(err?.response?.data?.detail || 'Failed to remove avatar.');
    } finally {
      setAvatarSaving(false);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <UserCircleIcon className="h-5 w-5 text-module-identity" />
          <span className="text-sm font-semibold text-gray-700">Assistant</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          How your assistant shows up across the app — name and avatar.
        </p>

        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <div className="h-16 w-16 rounded-full bg-module-identity/10 border border-module-identity/20 flex items-center justify-center overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Assistant avatar" className="h-full w-full object-cover" />
              ) : (
                <UserCircleIcon className="h-10 w-10 text-module-identity/50" />
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAvatarPick}
                disabled={avatarSaving}
                className="px-2.5 py-1 text-xs font-medium text-module-identity border border-module-identity/30 rounded-lg hover:bg-module-identity/10 disabled:opacity-50"
              >
                {avatarSaving ? 'Saving...' : avatarUrl ? 'Change photo' : 'Upload photo'}
              </button>
              {avatarUrl && (
                <button
                  type="button"
                  onClick={handleAvatarDelete}
                  disabled={avatarSaving}
                  className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                  title="Remove photo"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarChange}
              className="hidden"
            />
            {avatarError && <p className="text-xs text-red-600">{avatarError}</p>}
          </div>
        </div>

        <form onSubmit={handleSave} className="mt-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Assistant name</span>
            <input
              type="text"
              value={identityName}
              onChange={(e) => setIdentityName(e.target.value)}
              placeholder="e.g. Quina"
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-module-identity"
            />
          </label>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <InformationCircleIcon className="h-5 w-5 text-module-identity" />
          <span className="text-sm font-semibold text-gray-700">About Identity</span>
        </div>
        <p className="text-sm text-gray-500 leading-relaxed">
          The Identity module is your True Identity store — the centralized markdown files
          that define who your AI agent is across all engines (Cursor, OpenClaw, Claude).
          Files are stored in GCS and synced via the IDEM protocol. Each user can name
          their instance root differently.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Cog6ToothIcon className="h-5 w-5 text-module-identity" />
          <span className="text-sm font-semibold text-gray-700">Instance layout</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Configure where your instance lives inside the Identity store. The Overview
          dashboard reads STATE.md from the resolved path below.
        </p>

        {loading ? (
          <div className="space-y-3" aria-busy="true" aria-label="Loading">
            <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
            <div className="h-10 w-full animate-pulse rounded-lg bg-gray-100" />
            <div className="h-3 w-40 animate-pulse rounded bg-gray-100" />
            <div className="h-10 w-full animate-pulse rounded-lg bg-gray-100" />
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600">Instance root</span>
              <input
                type="text"
                value={instanceRoot}
                onChange={(e) => setInstanceRoot(e.target.value)}
                placeholder='e.g. Quina_Brain (empty = identity root)'
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-module-identity"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">STATE path (relative to root)</span>
              <input
                type="text"
                value={stateRelpath}
                onChange={(e) => setStateRelpath(e.target.value)}
                placeholder="self/STATE.md"
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-module-identity"
              />
            </label>
            {statePath && (
              <p className="text-xs text-gray-400">
                Resolved STATE path: <span className="font-mono text-gray-600">{statePath}</span>
              </p>
            )}
            {error && <p className="text-xs text-red-600">{error}</p>}
            {savedMsg && <p className="text-xs text-green-600">{savedMsg}</p>}
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-module-identity rounded-lg hover:bg-module-identity/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </form>
        )}
      </div>

      {summary?.by_folder?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">Folder breakdown</p>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {summary.by_folder.map(({ folder, count }) => (
              <div key={folder} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2">
                  <FolderIcon className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm text-gray-700">{folder}/</span>
                </div>
                <span className="text-xs text-gray-400">{count} {count === 1 ? 'file' : 'files'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MainSettings;
